
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { trackFormSubmit } from "@/lib/analytics";
import { resetConsent } from "@/lib/consent";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast({
        title: "Error",
        description: "Por favor introduce tu email.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (!res.ok) throw new Error('Error en el servidor');
      trackFormSubmit('newsletter');

      toast({
        title: "¡Suscrito!",
        description: "Gracias por suscribirte a nuestro newsletter.",
      });
      setEmail("");
    } catch (error) {
      console.error('Error subscribing:', error);
      toast({
        title: "Error",
        description: "Hubo un problema al suscribirte. Por favor, inténtalo de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer id="contact" className="bg-[hsl(var(--navbar-graphite))] text-white pt-16 pb-8 w-full">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-10 pb-10 border-b border-gray-700">
          <div className="lg:col-span-2">
            <img
              src="/lovable-uploads/051ebda6-5d88-45e1-a233-5db94541da02.webp"
              alt="D&J Partners Logo"
              width="192"
              height="192"
              loading="lazy"
              className="h-24 w-auto mb-6 brightness-0 invert"
            />
            <p className="text-gray-300 mb-6">
              Consultora especializada en transformación digital mediante IA, Business Intelligence y análisis predictivo para organizaciones que buscan ventaja competitiva sostenible.
            </p>
            <p className="text-gray-300 mb-6">
              Barcelona, España<br />
              Consultoría disponible a nivel nacional
            </p>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-4 text-white">Empresa</h3>
            <ul className="space-y-3">
              <li><Link to="/about" className="text-gray-300 hover:text-white transition-colors">Nosotros</Link></li>
              <li><Link to="/careers" className="text-gray-300 hover:text-white transition-colors">Únete al Equipo</Link></li>
              <li><Link to="/privacy-policy" className="text-gray-300 hover:text-white transition-colors">Política de Privacidad</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-4 text-white">Mantente Informado</h3>
            <form className="space-y-4" onSubmit={handleSubscribe}>
              <div>
                <input
                  type="email"
                  placeholder="Tu email"
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-600 text-white placeholder-gray-400"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
              <button
                type="submit"
                className="w-full px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Suscribiendo..." : (
                  <>
                    Suscribirse
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        <div className="pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-400 text-sm mb-4 md:mb-0">
            © {new Date().getFullYear()} D&J Partners. Todos los derechos reservados.
          </p>
          <div className="flex space-x-6">
            <Link to="/privacy-policy" className="text-sm text-gray-400 hover:text-white transition-colors">Política de Privacidad</Link>
            <button
              type="button"
              onClick={resetConsent}
              className="text-sm text-gray-400 hover:text-white transition-colors"
            >
              Configurar cookies
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
